// Shared by the server layout (cookie read) and the client sidebar, so this
// module must stay free of 'use client'.
import designTokens from '../../styles/design-tokens.json';

const px = (value: string) => Number.parseInt(value, 10);

export const SIDEBAR_OPEN_COOKIE = 'lqd-sidebar-open';
export const SIDEBAR_WIDTH_COOKIE = 'lqd-sidebar-width';
export const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export const SIDEBAR_DEFAULT_WIDTH = px(designTokens.layout.sidebar.$value);
export const SIDEBAR_MIN_WIDTH = px(designTokens.layout['sidebar-min'].$value);
export const SIDEBAR_MAX_WIDTH = px(designTokens.layout['sidebar-max'].$value);
export const SIDEBAR_INSET = px(designTokens.component['sidebar-inset'].$value);

export const SIDEBAR_STEP = 8;
export const SIDEBAR_LARGE_STEP = 32;
// Dragging this far past the minimum width snaps the sidebar closed.
export const SIDEBAR_COLLAPSE_THRESHOLD = 56;

export function clampSidebarWidth(width: number) {
  const value = Number.isFinite(width) ? width : SIDEBAR_DEFAULT_WIDTH;
  return Math.round(
    Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, value))
  );
}

export function resolveSidebarOpen(value?: string) {
  return value !== 'false';
}

/** Digits only, then clamped: the result is written into an inline style. */
export function resolveSidebarWidth(value?: string) {
  return value && /^\d{2,4}$/.test(value)
    ? clampSidebarWidth(Number(value))
    : SIDEBAR_DEFAULT_WIDTH;
}

/** A collapsing drag keeps the start width so reopening restores it. */
export function resolveSidebarDrag(start: number, delta: number) {
  const raw = start + delta;
  return raw < SIDEBAR_MIN_WIDTH - SIDEBAR_COLLAPSE_THRESHOLD
    ? { width: start, collapsed: true }
    : { width: clampSidebarWidth(raw), collapsed: false };
}

export function sidebarWidthForKey(
  width: number,
  key: string,
  shiftKey: boolean
): number | null {
  const step = shiftKey ? SIDEBAR_LARGE_STEP : SIDEBAR_STEP;
  switch (key) {
    case 'ArrowLeft':
      return clampSidebarWidth(width - step);
    case 'ArrowRight':
      return clampSidebarWidth(width + step);
    case 'Home':
      return SIDEBAR_MIN_WIDTH;
    case 'End':
      return SIDEBAR_MAX_WIDTH;
    case 'Enter':
      return SIDEBAR_DEFAULT_WIDTH;
    default:
      return null;
  }
}

export function writeSidebarCookie(
  name: string,
  value: string | number | boolean
) {
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${value}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}
